import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShieldCheck, LogIn, User2, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";

export default function Header() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const location = useLocation();

    const nav = [
        { to: "/", label: "Start" },
        { to: "/check", label: "Check starten" },
        { to: "/pakete", label: "Beratungspakete" },
        { to: "/ratgeber", label: "Ratgeber" },
        { to: "/faq", label: "FAQ" },
    ];

    return (
        <header className="sticky top-0 z-40 w-full border-b border-ink-300/30 bg-white/85 backdrop-blur-xl">
            <div className="container flex h-16 items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-500 text-white">
                        <ShieldCheck className="h-5 w-5" />
                    </span>
                    <div className="leading-tight">
                        <div className="font-display text-[15px] font-bold text-ink-900">
                            Pflegeheim-Kosten
                        </div>
                        <div className="text-[11px] text-ink-500">für Angehörige</div>
                    </div>
                </Link>

                <nav className="hidden items-center gap-1 lg:flex">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `rounded-full px-4 py-2 text-sm font-medium transition ${
                                    isActive
                                        ? "bg-brand-50 text-brand-700"
                                        : "text-ink-700 hover:bg-brand-50/60"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden items-center gap-2 lg:flex">
                    {user ? (
                        <>
                            <Link to="/meine-dokumente" data-testid="header-documents">
                                <Button variant="ghost" className="rounded-full">Dokumente</Button>
                            </Link>
                            <Link to="/konto" data-testid="header-account">
                                <Button variant="ghost" className="rounded-full gap-2">
                                    <User2 className="h-4 w-4" />
                                    {user.name || "Konto"}
                                </Button>
                            </Link>
                            {user.role === "admin" && (
                                <Link to="/admin" data-testid="header-admin">
                                    <Button variant="outline" className="rounded-full">Admin</Button>
                                </Link>
                            )}
                            <Button
                                variant="ghost"
                                className="rounded-full gap-2"
                                onClick={logout}
                                data-testid="header-logout"
                            >
                                <LogOut className="h-4 w-4" /> Abmelden
                            </Button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" data-testid="header-login">
                                <Button variant="ghost" className="rounded-full gap-2">
                                    <LogIn className="h-4 w-4" /> Anmelden
                                </Button>
                            </Link>
                            <Link to="/check" data-testid="header-cta-check">
                                <Button className="rounded-full bg-brand-500 hover:bg-brand-600">
                                    Jetzt kostenlos prüfen
                                </Button>
                            </Link>
                        </>
                    )}
                </div>

                <button
                    className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-ink-300/40"
                    onClick={() => setOpen((s) => !s)}
                    aria-label="Menü"
                    data-testid="header-mobile-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-ink-300/30 bg-white">
                    <div className="container flex flex-col py-3">
                        {nav.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `rounded-xl px-4 py-3 text-base font-medium ${
                                        isActive ? "bg-brand-50 text-brand-700" : "text-ink-900"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <div className="my-2 h-px bg-ink-300/30" />
                        {user ? (
                            <>
                                <Link to="/konto" onClick={() => setOpen(false)} className="rounded-xl px-4 py-3 text-base">
                                    Mein Konto
                                </Link>
                                {user.role === "admin" && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="rounded-xl px-4 py-3 text-base">
                                        Admin
                                    </Link>
                                )}
                                <button
                                    onClick={() => {
                                        logout();
                                        setOpen(false);
                                    }}
                                    className="text-left rounded-xl px-4 py-3 text-base"
                                    data-testid="mobile-logout"
                                >
                                    Abmelden
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    onClick={() => setOpen(false)}
                                    className="rounded-xl px-4 py-3 text-base"
                                    data-testid="mobile-login"
                                >
                                    Anmelden
                                </Link>
                                <Link
                                    to="/check"
                                    onClick={() => setOpen(false)}
                                    className="mt-2 rounded-full bg-brand-500 px-4 py-3 text-center text-base font-semibold text-white"
                                    data-testid="mobile-cta-check"
                                >
                                    Jetzt kostenlos prüfen
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Trust-Bar (not on check page) */}
            {!location.pathname.startsWith("/check") && (
                <div className="bg-brand-50/60 text-xs text-ink-700">
                    <div className="container flex flex-wrap items-center justify-center gap-x-6 gap-y-1 py-1.5 text-center">
                        <span>Diskret &amp; vertraulich</span>
                        <span className="hidden sm:inline">•</span>
                        <span>Keine automatisierte Rechtsberatung</span>
                        <span className="hidden sm:inline">•</span>
                        <span>DSGVO-konform</span>
                    </div>
                </div>
            )}
        </header>
    );
}
